import React from "react";
import {Card, Col, Row, Spinner} from "react-bootstrap";
import {PiStack} from "react-icons/pi";
import useStatisticsData from "./useStatisticsData.js";
import HelpTooltip from "../HelpTooltip.jsx";
import {RECOMMENDATION_META, RECOMMENDATION_ORDER, formatPallets} from "./inventoryStrategyShared.jsx";
import {TREND_NEUTRAL_COLOR, TREND_UP_COLOR, GROUP_PALETTE} from "./format.js";

const PALLET_METRICS = [
    {
        key: "pallets_current",
        label: "Belegte Paletten heute",
        color: TREND_NEUTRAL_COLOR,
        help: "Summe der aktuell belegten Paletten (nur Warengruppen mit bekannter Palettengröße, nur SNV Weilheim).",
    },
    {
        key: "pallets_target",
        label: "Empfohlene Paletten",
        color: GROUP_PALETTE[0],
        help: "Summe der Paletten, die der empfohlene Zielbestand je Artikel ergibt.",
    },
    {
        key: "pallets_savable",
        label: "Einsparbare Paletten",
        color: TREND_UP_COLOR,
        help: "Summe des tatsächlich abbaubaren Überbestands in Paletten - kein Saldo mit Fehlbeständen anderer Artikel.",
    },
];

/**
 * Kennzahlen-Kacheln der Lagerstrategie: Anzahl Artikel je Einstufung sowie
 * die drei Palettenkennzahlen aus InventoryStrategyQuery::summary(). Farbe
 * ist bei den Einstufungs-Kacheln nie allein tragend - Symbol und Text stehen
 * immer daneben (siehe RECOMMENDATION_META).
 */
export default function InventoryKpiCards({params}) {
    const {data, loading, error} = useStatisticsData("/statistics/inventory-strategy/summary", params);
    const overall = data?.overall;

    return (
        <div>
            <Row xs={2} sm={3} lg={4} xl={5} className="g-3">
                {RECOMMENDATION_ORDER.map((key) => {
                    const meta = RECOMMENDATION_META[key];
                    const Icon = meta.Icon;
                    const count = overall?.by_recommendation?.[key];
                    return (
                        <Col key={key}>
                            <Card className="h-100 shadow-sm">
                                <Card.Body>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <span className="text-muted small text-uppercase d-inline-flex align-items-center gap-1">
                                            <Icon aria-hidden="true" style={{color: meta.color}} />
                                            {meta.label}
                                        </span>
                                    </div>
                                    {loading && !data ? (
                                        <div className="py-2">
                                            <Spinner animation="border" size="sm" role="status">
                                                <span className="visually-hidden">Wird geladen…</span>
                                            </Spinner>
                                        </div>
                                    ) : (
                                        <div className="fs-4 fw-semibold lh-sm mt-1">{count ?? "–"} Artikel</div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}

                {PALLET_METRICS.map((metric) => (
                    <Col key={metric.key}>
                        <Card className="h-100 shadow-sm">
                            <Card.Body>
                                <div className="d-flex align-items-center justify-content-between">
                                    <span className="text-muted small text-uppercase d-inline-flex align-items-center gap-1">
                                        <PiStack aria-hidden="true" style={{color: metric.color}} />
                                        {metric.label}
                                    </span>
                                    <HelpTooltip placement="left" size={0.75}>{metric.help}</HelpTooltip>
                                </div>
                                {loading && !data ? (
                                    <div className="py-2">
                                        <Spinner animation="border" size="sm" role="status">
                                            <span className="visually-hidden">Wird geladen…</span>
                                        </Spinner>
                                    </div>
                                ) : (
                                    <div className="fs-4 fw-semibold lh-sm mt-1">{formatPallets(overall?.[metric.key])}</div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>
            {error && <div className="text-danger small mt-2">{error}</div>}
            {data?.overall && (
                <div className="text-muted small mt-2">
                    {data.overall.article_count} Artikel insgesamt, davon {data.overall.articles_with_known_pallet_size} mit
                    bekannter Palettengröße. Bezugsmonat {data.reference?.reference_month}.
                </div>
            )}
        </div>
    );
}
